import React, { useState } from 'react';
import {
    View,
    Text,
    ScrollView,
    Image,
    TouchableOpacity,
    StyleSheet,
    Dimensions,
    Platform,
    Alert,
    SafeAreaView
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { StatusBar } from 'expo-status-bar';
import MarketplaceFooter from '../../components/common/MarketplaceFooter';
import { getShadow } from '../../theme/useTheme';
import { H2, H3, Caption, Body1, Body2 } from '../../components/common/TextStyles';

const { width, height } = Dimensions.get('window');

const ServiceDetailScreen = ({ route, navigation }) => {
    const { service } = route.params;
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [activeTab, setActiveTab] = useState('overview'); // overview, portfolio, reviews

    const images = (() => {
        if (!service.images) return ['https://images.unsplash.com/photo-1581578731548-c64695cc6954?auto=format&fit=crop&q=80&w=800'];
        try {
            const parsed = typeof service.images === 'string' ? JSON.parse(service.images) : service.images;
            const arr = Array.isArray(parsed) ? parsed : [parsed];
            return arr.length > 0 ? arr : ['https://images.unsplash.com/photo-1581578731548-c64695cc6954?auto=format&fit=crop&q=80&w=800'];
        } catch (e) {
            return [service.images];
        }
    })();

    const config = service.service_config ? (typeof service.service_config === 'string' ? JSON.parse(service.service_config) : service.service_config) : {};

    const handleBookNow = () => {
        navigation.navigate('ServiceCheckout', { service });
    };

    const handleMessage = () => {
        // Logic to open chat with provider
        Alert.alert("Chat", "Opening chat with provider...");
    };

    const renderHeader = () => (
        <View style={styles.header}>
            <TouchableOpacity
                style={styles.backButton}
                onPress={() => navigation.goBack()}
            >
                <Ionicons name="chevron-back" size={20} color="#1F2937" />
            </TouchableOpacity>
            <View style={styles.headerActions}>
                <TouchableOpacity style={styles.headerIcon}>
                    <Ionicons name="heart-outline" size={20} color="#1F2937" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.headerIcon}>
                    <Ionicons name="share-outline" size={20} color="#1F2937" />
                </TouchableOpacity>
            </View>
        </View>
    );

    return (
        <View style={styles.container}>
            <StatusBar style="dark" />
            {renderHeader()}

            <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={styles.scrollContent}>
                {/* Hero Image Gallery */}
                <View style={styles.galleryContainer}>
                    <ScrollView
                        horizontal
                        pagingEnabled
                        showsHorizontalScrollIndicator={false}
                        onMomentumScrollEnd={(e) => {
                            const newIndex = Math.round(e.nativeEvent.contentOffset.x / width);
                            setSelectedImageIndex(newIndex);
                        }}
                    >
                        {images.map((img, index) => (
                            <Image
                                key={index}
                                source={{ uri: img }}
                                style={styles.mainImage}
                            />
                        ))}
                    </ScrollView>
                    <View style={styles.pagination}>
                        {images.map((_, index) => (
                            <View
                                key={index}
                                style={[
                                    styles.paginationDot,
                                    index === selectedImageIndex && styles.paginationDotActive
                                ]}
                            />
                        ))}
                    </View>
                </View>

                {/* Service Title & Primary Info */}
                <View style={styles.mainInfo}>
                    <View style={styles.badgeRow}>
                        <View style={styles.serviceBadge}>
                            <Ionicons name="sparkles" size={10} color="#6366F1" />
                            <Text style={styles.serviceBadgeText}>PROFESSIONAL SERVICE</Text>
                        </View>
                        <View style={styles.ratingBadge}>
                            <Ionicons name="star" size={10} color="#F59E0B" />
                            <Text style={styles.ratingText}>{service.rating || '5.0'}</Text>
                        </View>
                    </View>

                    <H2 style={styles.title}>{service.name}</H2>

                    <View style={styles.priceRow}>
                        <Text style={styles.price}>
                            <Text style={styles.currency}>KES </Text>
                            {Number(service.price).toLocaleString()}
                        </Text>
                        <Text style={styles.unit}> / {service.unit || 'project'}</Text>
                    </View>

                    {/* Service Feature Pills */}
                    <View style={styles.featuresRow}>
                        <View style={styles.featurePill}>
                            <Ionicons name="time-outline" size={14} color="#64748B" />
                            <Text style={styles.featurePillText}>{config.duration_estimate || 'Flexible'}</Text>
                        </View>
                        <View style={styles.featurePill}>
                            <Ionicons name="location-outline" size={14} color="#64748B" />
                            <Text style={styles.featurePillText}>{config.location_type || 'On-Site/Online'}</Text>
                        </View>
                    </View>
                </View>

                {/* Tabs */}
                <View style={styles.tabsContainer}>
                    {['overview', 'portfolio', 'reviews'].map((tab) => (
                        <TouchableOpacity
                            key={tab}
                            style={[styles.tab, activeTab === tab && styles.activeTab]}
                            onPress={() => setActiveTab(tab)}
                        >
                            <Text style={[styles.tabText, activeTab === tab && styles.activeTabText]}>
                                {tab.toUpperCase()}
                            </Text>
                        </TouchableOpacity>
                    ))}
                </View>

                {/* Tab Content */}
                <View style={styles.tabContent}>
                    {activeTab === 'overview' && (
                        <View>
                            <H3 style={styles.sectionTitle}>Description</H3>
                            <Body2 style={styles.description}>
                                {service.description || "Looking for top-notch professional results? I provide expert services tailored to your needs. Highly reliable and community-vetted."}
                            </Body2>

                            <H3 style={[styles.sectionTitle, { marginTop: 20 }]}>What's Included</H3>
                            <View style={styles.includedGrid}>
                                {['Verified Expertise', 'Quality Guarantee', 'On-time Delivery', 'Support'].map((item, i) => (
                                    <View key={i} style={styles.includedItem}>
                                        <Ionicons name="checkmark-circle" size={16} color="#10B981" />
                                        <Text style={styles.includedText}>{item}</Text>
                                    </View>
                                ))}
                            </View>
                        </View>
                    )}

                    {activeTab === 'portfolio' && (
                        <View style={styles.portfolioGrid}>
                            {images.map((img, i) => (
                                <Image key={i} source={{ uri: img }} style={styles.portfolioImg} />
                            ))}
                        </View>
                    )}

                    {activeTab === 'reviews' && (
                        <View style={styles.reviewsList}>
                            <View style={styles.emptyReviews}>
                                <Ionicons name="chatbubbles-outline" size={32} color="#CBD5E1" />
                                <Text style={styles.emptyReviewsText}>No reviews yet. Be the first to book!</Text>
                            </View>
                        </View>
                    )}
                </View>

                <View style={styles.footerGap} />
            </ScrollView>

            {/* Sticky Action Bar */}
            <View style={styles.actionBar}>
                <TouchableOpacity style={styles.messageBtn} onPress={handleMessage}>
                    <Ionicons name="chatbubble-ellipses-outline" size={20} color="#6366F1" />
                </TouchableOpacity>
                <TouchableOpacity style={styles.bookBtn} onPress={handleBookNow}>
                    <LinearGradient
                        colors={['#6366F1', '#4F46E5']}
                        style={styles.bookGradient}
                        start={{ x: 0, y: 0 }}
                        end={{ x: 1, y: 0 }}
                    >
                        <Text style={styles.bookText}>Check Availability & Book</Text>
                        <Ionicons name="arrow-forward" size={18} color="#FFF" />
                    </LinearGradient>
                </TouchableOpacity>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#FFFFFF',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingHorizontal: 20,
        paddingTop: Platform.OS === 'ios' ? 50 : 40,
        paddingBottom: 15,
        backgroundColor: '#FFF',
        zIndex: 10,
    },
    backButton: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: '#F8FAFB',
        alignItems: 'center',
        justifyContent: 'center',
    },
    headerActions: {
        flexDirection: 'row',
        gap: 10,
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        backgroundColor: '#F8FAFB',
        alignItems: 'center',
        justifyContent: 'center',
    },
    scrollContent: {
        paddingBottom: 100,
    },
    galleryContainer: {
        width: width,
        height: 280,
    },
    mainImage: {
        width: width,
        height: 280,
        resizeMode: 'cover',
    },
    pagination: {
        flexDirection: 'row',
        position: 'absolute',
        bottom: 20,
        left: 20,
    },
    paginationDot: {
        width: 6,
        height: 6,
        borderRadius: 3,
        backgroundColor: 'rgba(255,255,255,0.4)',
        marginRight: 6,
    },
    paginationDotActive: {
        width: 20,
        backgroundColor: '#FFFFFF',
    },
    mainInfo: {
        padding: 20,
    },
    badgeRow: {
        flexDirection: 'row',
        gap: 10,
        marginBottom: 12,
    },
    serviceBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#EEF2FF',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        gap: 4,
    },
    serviceBadgeText: {
        fontSize: 10,
        fontWeight: '800',
        color: '#6366F1',
    },
    ratingBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FFF7ED',
        paddingHorizontal: 8,
        paddingVertical: 4,
        borderRadius: 6,
        gap: 4,
    },
    ratingText: {
        fontSize: 10,
        fontWeight: '800',
        color: '#F59E0B',
    },
    title: {
        fontSize: 24,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 10,
    },
    priceRow: {
        flexDirection: 'row',
        alignItems: 'baseline',
        marginBottom: 15,
    },
    price: {
        fontSize: 22,
        fontWeight: '900',
        color: '#6366F1',
    },
    currency: {
        fontSize: 14,
        fontWeight: '700',
    },
    unit: {
        fontSize: 14,
        color: '#64748B',
        fontWeight: '600',
    },
    featuresRow: {
        flexDirection: 'row',
        gap: 12,
    },
    featurePill: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#F8FAFB',
        borderWidth: 1,
        borderColor: '#F1F5F9',
        paddingHorizontal: 12,
        paddingVertical: 8,
        borderRadius: 12,
        gap: 6,
    },
    featurePillText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#475569',
    },
    tabsContainer: {
        flexDirection: 'row',
        paddingHorizontal: 20,
        borderBottomWidth: 1,
        borderBottomColor: '#F1F5F9',
        gap: 25,
    },
    tab: {
        paddingVertical: 12,
    },
    activeTab: {
        borderBottomWidth: 2,
        borderBottomColor: '#6366F1',
    },
    tabText: {
        fontSize: 12,
        fontWeight: '700',
        color: '#94A3B8',
    },
    activeTabText: {
        color: '#6366F1',
    },
    tabContent: {
        padding: 20,
    },
    sectionTitle: {
        fontSize: 16,
        fontWeight: '800',
        color: '#111827',
        marginBottom: 8,
    },
    description: {
        fontSize: 14,
        lineHeight: 22,
        color: '#4B5563',
    },
    includedGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 12,
    },
    includedItem: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
        width: '45%',
    },
    includedText: {
        fontSize: 13,
        color: '#374151',
        fontWeight: '600',
    },
    portfolioGrid: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 10,
    },
    portfolioImg: {
        width: (width - 50) / 2,
        height: 120,
        borderRadius: 12,
    },
    reviewsList: {
        paddingVertical: 10,
    },
    emptyReviews: {
        alignItems: 'center',
        paddingVertical: 40,
    },
    emptyReviewsText: {
        marginTop: 10,
        color: '#94A3B8',
        fontSize: 13,
    },
    footerGap: {
        height: 60,
    },
    actionBar: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        flexDirection: 'row',
        paddingHorizontal: 20,
        paddingVertical: 20,
        paddingBottom: Platform.OS === 'ios' ? 35 : 20,
        backgroundColor: '#FFF',
        borderTopWidth: 1,
        borderTopColor: '#F1F5F9',
        gap: 12,
        alignItems: 'center',
    },
    messageBtn: {
        width: 50,
        height: 50,
        borderRadius: 14,
        borderWidth: 1.5,
        borderColor: '#E2E8F0',
        alignItems: 'center',
        justifyContent: 'center',
    },
    bookBtn: {
        flex: 1,
        height: 50,
        borderRadius: 14,
        overflow: 'hidden',
        ...getShadow('md'),
    },
    bookGradient: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
    },
    bookText: {
        color: '#FFF',
        fontSize: 16,
        fontWeight: '800',
    },
});

export default ServiceDetailScreen;
